import { Component, OnInit } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { CrudOps } from '../../../../api/constants/crud.enum'
import { EmployeePaginatedList } from '../../../../api/models/employee.model'
import { UserAccount } from '../../../../api/models/user-account.model'
import { AccountService } from '../../../../api/services/accounts.service'
import { EmployeeService } from '../../../../api/services/employee.service'
import { DialogService } from '../../../common/services/dialog.service'
import { LoaderService } from '../../../common/services/loader.service'
import { PpwLocalStorageService } from '../../../common/services/ppwlocalstorage.service'
import { SnackbarService } from '../../../common/services/snackbar.service'
import { UnsubscribeOnDestroyAdapter } from '../../../shared/adapter/unsubscribe-on-destroy-adapter'
import { AccountDialogComponent } from '../account-dialog/account-dialog.component'

@Component({
    selector: 'ppw-app-accounts',
    templateUrl: './accounts.component.html',
    styleUrls: ['./accounts.component.scss']
})
export class AccountsComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    pageTitle = 'Accounts'
    userAccounts$: Observable<Array<UserAccount> | undefined>
    selectedUserAccount?: UserAccount
    employeeSubjectList!: EmployeePaginatedList
    displayedColumns: Array<string> = [
        'userName',
        'firstName',
        'lastName',
        'identityRoles',
        'isLocked',
        'accessFailedCounter'
    ]

    private readonly userAccountSubject: BehaviorSubject<Array<UserAccount> | undefined> = new BehaviorSubject<
        Array<UserAccount> | undefined
    >(undefined)
    private readonly prefix = 'AccountsComponent'

    constructor(
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        private readonly dialogService: DialogService,
        private readonly snackBar: SnackbarService,
        private readonly loaderService: LoaderService,
        private readonly accountService: AccountService,
        private readonly snackbarService: SnackbarService,
        readonly employeeService: EmployeeService
    ) {
        super()
        this.userAccounts$ = this.userAccountSubject.asObservable()
    }

    async ngOnInit(): Promise<void> {
        await this.fetchAllUserAccountsAndEmployees()
    }

    createAccount(): void {
        this.openAccountDialog(CrudOps.Create, this.employeeSubjectList, undefined)
    }

    editAccount(): void {
        if (this.selectedUserAccount) {
            this.openAccountDialog(
                this.selectedUserAccount.isSystemAccount ? CrudOps.Read : CrudOps.Update,
                this.employeeSubjectList,
                this.selectedUserAccount
            )
        }
    }

    deleteAccount(): void {
        if (this.selectedUserAccount) {
            this.openAccountDialog(CrudOps.Delete, this.employeeSubjectList, this.selectedUserAccount)
        }
    }

    select(userAccount: UserAccount): void {
        if (this.selectedUserAccount !== userAccount) {
            this.selectedUserAccount = userAccount
            this.ppwLocalStorageService.storeData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_USERACCOUNT_ID,
                this.selectedUserAccount.id
            )
        } else {
            this.selectedUserAccount = undefined
            this.ppwLocalStorageService.removeStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_USERACCOUNT_ID.key
            )
        }
    }

    editDoubleClick(userAccount: UserAccount): void {
        this.selectedUserAccount = userAccount
        this.ppwLocalStorageService.storeData(
            this.ppwLocalStorageService.STORAGE_KEY_SELECTED_USERACCOUNT_ID,
            this.selectedUserAccount.id
        )
        this.editAccount()
    }

    private async fetchAllUserAccountsAndEmployees(): Promise<void> {
        this.loaderService.startLoading()
        try {
            const userAccountList = await this.accountService.getAllAccounts()
            this.employeeSubjectList = await this.employeeService.getAllEmployees()
            this.userAccountSubject.next(userAccountList.items)
            const storedUserAccountId = this.ppwLocalStorageService.getStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_EMPLOYEE_ID
            )
            const foundUserAccount = userAccountList.items.find((userAccount) => userAccount.id === storedUserAccountId)
            if (foundUserAccount) {
                this.selectedUserAccount = foundUserAccount
            }
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    private openAccountDialog(
        crud: CrudOps,
        employeeSubjectList: EmployeePaginatedList,
        userAccount?: UserAccount
    ): void {
        this.subscriptions.add(
            this.dialogService
                .open(AccountDialogComponent, { userAccount, crud, employeeSubjectList })
                .afterClosed()
                .subscribe((crudResponse: CrudOps) => {
                    if (crudResponse !== undefined) {
                        if (crudResponse === CrudOps.Create) {
                            this.snackBar.promtSnackBar('Account created')
                        } else if (crudResponse === CrudOps.Update) {
                            this.snackBar.promtSnackBar('Account updated')
                        } else if (crudResponse === CrudOps.Delete) {
                            this.snackBar.promtSnackBar('Account deleted')
                        }

                        this.fetchAllUserAccountsAndEmployees()
                    }
                })
        )
    }
}
